import { STRINGS } from '@/strings/strings';
import type { StoryErrorBlockProps } from '@/components/patterns/StoryErrorBlock';

/**
 * Factory builders that map a Story-Output error variant to the
 * full set of `<StoryErrorBlock />` props (illustration src + alt,
 * title, body, primary/secondary CTAs). Routing from backend
 * `error_code` → variant lives in `pwa/src/lib/errors.ts`
 * (`getErrorVariant`).
 *
 * Sub-phase B-1 ships 3 variants: technical / limitBlock /
 * storyNotFound. Timeout deferred to post-launch (tracker PWA Offen
 * #56).
 */

/**
 * Self-hosted error illustrations expected at
 * `pwa/public/errorimages/`. Asset upload tracked separately by
 * Sebastian — the build proceeds with these source paths and the
 * browser shows a broken-image icon until the files land. Filename
 * verification is a Sub-phase B follow-up step.
 *
 * If filenames change at upload time, update only these constants
 * in this file — no other code changes required.
 */
// Sebastian-uploaded 2026-04-27. Filenames verified post-upload.
export const FLITZI_IMAGE_SRC = '/errorimages/flitzi.jpg';
export const KAEPTN_KUSCHEL_IMAGE_SRC = '/errorimages/kaeptn_kuschel.png';

export interface VariantHandlers {
  /** Fired by the technical-failure "Erneut versuchen" CTA. */
  onRetry: () => void;
  /** Fired by the storyNotFound + (Limit-Block secondary, if mapped) → /library. */
  onGoToLibrary: () => void;
  /** Fired by the Limit-Block primary "Abo abschließen" CTA. */
  onChoosePlan: () => void;
  /** Fired by the Limit-Block secondary "Einmalpaket kaufen" CTA. */
  onBuyPack: () => void;
}

/**
 * Build `<StoryErrorBlock />` props for the technical-failure variant
 * (Briefing Kap. 8 line 1183-1189). Flitzi + "Oops!" + Retry CTA.
 */
export function technicalErrorVariant(handlers: VariantHandlers): StoryErrorBlockProps {
  const S = STRINGS.storyOutput.errorStates.technical;
  return {
    illustrationSrc: FLITZI_IMAGE_SRC,
    illustrationAlt: S.illustrationAlt,
    title: S.title,
    body: S.body,
    primaryCta: { label: S.ctaPrimary, onClick: handlers.onRetry },
  };
}

/**
 * Build `<StoryErrorBlock />` props for the Limit-Block (Option B)
 * variant — defensive surface in OutputRoute when generation fails
 * with `error_code: 'blocked_limit'` (mid-flow credit drain race).
 *
 * Reuses `STRINGS.builder.errorBanners.limitTrial.*` per Sebastian
 * Gate-1 2026-04-27 — same copy as the Builder-side Option A,
 * different render location. Briefing Kap. 8 line 1191-1196.
 */
export function limitBlockErrorVariant(handlers: VariantHandlers): StoryErrorBlockProps {
  const S = STRINGS.builder.errorBanners.limitTrial;
  return {
    illustrationSrc: KAEPTN_KUSCHEL_IMAGE_SRC,
    illustrationAlt: `Käpt'n Kuschel`,
    title: S.title,
    body: S.body,
    primaryCta: { label: S.ctaPrimary, onClick: handlers.onChoosePlan },
    secondaryCta: { label: S.ctaSecondary, onClick: handlers.onBuyPack },
  };
}

/**
 * Build `<StoryErrorBlock />` props for the story-not-found variant
 * (Briefing Kap. 8 line 1206-1212). Flitzi + "Diese Geschichte gibt
 * es nicht mehr." + Zur-Bibliothek CTA.
 */
export function storyNotFoundErrorVariant(handlers: VariantHandlers): StoryErrorBlockProps {
  const S = STRINGS.storyOutput.errorStates.storyNotFound;
  return {
    illustrationSrc: FLITZI_IMAGE_SRC,
    illustrationAlt: S.illustrationAlt,
    title: S.title,
    body: S.body,
    primaryCta: { label: S.ctaPrimary, onClick: handlers.onGoToLibrary },
  };
}
